import { Button, Loader, useComputedColorScheme } from '@mantine/core';
import * as Sentry from '@sentry/react';
import { IconPrinter } from '@tabler/icons-react';
import { useSendFax } from 'api/action/fax';
import { Fax, FaxStatus } from 'interfaces';
import { useState } from 'react';
import { useCurrentSiteStore, useCurrentUserStore } from 'store';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  constructFaxAttachmentS3Uri,
  normalizeFaxNumber,
} from 'utils/faxUtils';
import { nowISO } from 'utils/dateUtils';
import { validatePhoneNumber } from 'utils/validate';
import { v4 as uuidv4 } from 'uuid';

interface SendFaxButtonProps {
  attachmentBucket: string;
  attachmentKey: string;
  claimID?: string | null;
  recipient: string;
  disabled?: boolean;
  sendButtonLabel?: string;
  onBeforeSend?: () => Promise<boolean> | boolean;
  onSuccess?: () => void;
}

export const SendFaxButton = ({
  attachmentBucket,
  attachmentKey,
  claimID,
  recipient,
  disabled = false,
  sendButtonLabel = 'Mark Invoiced and Send Fax',
  onBeforeSend,
  onSuccess,
}: SendFaxButtonProps) => {
  const { id: userID } = useCurrentUserStore();
  const { id: siteID } = useCurrentSiteStore();
  const sendFax = useSendFax();
  const [isSendingFax, setIsSendingFax] = useState(false);
  const computedColorScheme = useComputedColorScheme();

  const normalizedRecipient = normalizeFaxNumber(recipient);
  const hasValidRecipient = validatePhoneNumber(normalizedRecipient);
  const hasAttachmentContext =
    Boolean(claimID) &&
    Boolean(attachmentBucket?.trim()) &&
    Boolean(attachmentKey?.trim());

  const isSendDisabled =
    disabled || isSendingFax || !hasValidRecipient || !hasAttachmentContext;

  const handleSendFax = async () => {
    if (isSendDisabled) {
      return;
    }

    try {
      setIsSendingFax(true);

      if (onBeforeSend) {
        const canProceed = await onBeforeSend();
        if (!canProceed) {
          return;
        }
      }

      const faxData: Fax = {
        claimID: claimID as string,
        attachmentKey: constructFaxAttachmentS3Uri(
          attachmentBucket,
          attachmentKey
        ),
        createdBy: userID,
        status: FaxStatus.Pending,
        recipient: normalizedRecipient,
        createdAt: nowISO(),
        id: uuidv4(),
        siteID,
      };

      await sendFax.mutateAsync({ faxData });
      onSuccess?.();
    } catch (error) {
      Sentry.setContext('component', { filename: 'SendFaxButton.tsx' });
      Sentry.captureException(error);
    } finally {
      setIsSendingFax(false);
    }
  };

  return (
    <Button
      variant="outline"
      radius="md"
      data-testid="send-fax"
      onClick={handleSendFax}
      color="green"
      c={buttonTextColor(computedColorScheme, isSendDisabled)}
      disabled={isSendDisabled}
      leftSection={
        isSendingFax ? (
          <Loader size="xs" data-testid="fax-sending-loader" />
        ) : (
          <IconPrinter size={16} />
        )
      }
    >
      {sendButtonLabel}
    </Button>
  );
};
